/**
 * Buy Credits modal — refreshed Feb 28, 2026.
 *
 * Visual polish per user reference screenshot. Functional changes:
 *   - Quick-pick cards are now selection toggles (not buy-now). The single
 *     "Pay now" button in the footer handles checkout, driven by either
 *     selectedPkgId OR customAmount.
 *   - Header: green-pill $ icon + "Buy credits" + X.
 *   - Balance row: soft-green pill.
 *   - Insight strip: Student unlocks · Months of Pro · Cost per credit
 *     (role-aware — Pro price differs for agent vs institution).
 *   - Footer: Total $X.XX · "How credits work ↗" link · 🔒 Pay now button
 *     (disabled until an amount is picked).
 *
 * All existing entry points (Ad Centre, Wallet header, EmptyWallet CTA) keep
 * working because the trigger contract `openPurchaseModal()` is unchanged.
 */
import { useState, useEffect, useMemo } from 'react';
import { API, useAuth } from '@/App';
import { useWallet } from '@/components/TopNav';
import axios from 'axios';
import {
  X, Loader2, Shield, DollarSign, Wallet, Lock,
} from 'lucide-react';
import { toast } from 'sonner';

export default function PurchaseCreditsModal({ open, onClose, trigger }) {
  const { user } = useAuth();
  const { balance, creditLimit, availableCredit } = useWallet();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [creditValue, setCreditValue] = useState(0.10);
  const [pricing, setPricing] = useState({ currency: 'USD', symbol: '$', credit_rate: 0.10, bonus_tiers: [], flash_sale: { pct: 0 } });
  const [selectedPkgId, setSelectedPkgId] = useState(null);
  const [customAmount, setCustomAmount] = useState('');

  useEffect(() => {
    if (open) {
      setLoading(true);
      setSelectedPkgId(null);
      setCustomAmount('');
      Promise.all([
        axios.get(`${API}/pricing/resolve`).catch(() => ({ data: null })),
      ]).then(([prRes]) => {
        const cfg = prRes.data || {};
        const rate = cfg.credit_rate || 0.10;
        setCreditValue(rate);
        setPricing({
          currency: cfg.currency || 'USD',
          symbol: cfg.symbol || '$',
          credit_rate: rate,
          bonus_tiers: cfg.bonus_tiers || [],
          flash_sale: cfg.flash_sale || { pct: 0 },
          tax_pct: cfg.tax_pct || 0,
          tax_label: cfg.tax_label || '',
        });
        // Build quick picks from bonus tiers (per-country source of truth).
        // Falls back to legacy /credits/packages if no tiers are configured.
        const tiers = (cfg.bonus_tiers || []).filter(t => Number(t.min_spend) > 0);
        if (tiers.length) {
          const pkgs = tiers
            .slice()
            .sort((a, b) => Number(a.min_spend) - Number(b.min_spend))
            .map((t, i) => {
              const spend = Number(t.min_spend);
              const baseCredits = Math.floor(spend / rate);
              const bonus = Math.floor(baseCredits * (Number(t.bonus_pct) || 0) / 100);
              return {
                package_id: `tier_${i}_${spend}`,
                name: t.label || `${baseCredits + bonus} credits`,
                credits: baseCredits,
                bonus_credits: bonus,
                price_usd: spend,
                discount_percent: Number(t.bonus_pct) || 0,
                is_popular: (t.label || '').toLowerCase().includes('best'),
                is_active: true,
                _tier_label: t.label || '',
              };
            });
          setPackages(pkgs);
          setLoading(false);
        } else {
          axios.get(`${API}/credits/packages`).then(pkgRes => {
            setPackages((pkgRes.data.packages || []).filter(p => p.is_active !== false));
          }).catch(() => setPackages([])).finally(() => setLoading(false));
        }
      });
    }
  }, [open]);

  const selectedPkg = useMemo(
    () => packages.find(p => p.package_id === selectedPkgId),
    [packages, selectedPkgId],
  );

  // Active dollar amount + credits for the live insight strip + footer total
  const customUsd = parseFloat(customAmount) || 0;
  const totalUsd = selectedPkg ? selectedPkg.price_usd : customUsd;
  const totalCredits = selectedPkg
    ? (selectedPkg.credits || 0) + (selectedPkg.bonus_credits || 0)
    : (customUsd > 0 ? Math.floor(customUsd / creditValue) : 0);

  if (!open) return null;

  const handleSelectPkg = (pkgId) => {
    setSelectedPkgId(pkgId === selectedPkgId ? null : pkgId);
    setCustomAmount('');
  };

  const handleCustomChange = (v) => {
    setCustomAmount(v);
    setSelectedPkgId(null);
  };

  const handlePay = async () => {
    if (!totalUsd || totalUsd < 1) { toast.error('Pick a bundle or enter at least $1'); return; }
    setPurchasing(true);
    try {
      // Tier-based packages don't exist in credit_packages — fall back to
      // custom_amount_usd checkout. Legacy DB packages still use package_id.
      const isTier = selectedPkg && String(selectedPkg.package_id || '').startsWith('tier_');
      const body = (selectedPkg && !isTier)
        ? { package_id: selectedPkg.package_id, origin_url: window.location.href }
        : { custom_amount_usd: totalUsd, origin_url: window.location.href };
      const res = await axios.post(`${API}/credits/checkout`, body);
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('Checkout failed');
        setPurchasing(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start checkout');
      setPurchasing(false);
    }
  };

  // Compute a "save %" pill for each pack vs the cheapest one. Falls back
  // to the catalog's `discount_percent` when set.
  const savePctFor = (pkg) => {
    if (pkg.discount_percent) return pkg.discount_percent;
    if (!packages.length) return 0;
    const baseline = Math.min(...packages.map(p => (p.price_usd / ((p.credits || 1)))));
    const ours = pkg.price_usd / Math.max(1, pkg.credits + (pkg.bonus_credits || 0));
    if (ours >= baseline) return 0;
    return Math.round(((baseline - ours) / baseline) * 100);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl animate-in fade-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
        data-testid="purchase-credits-modal"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-emerald-50 flex items-center justify-center">
              <DollarSign className="h-4 w-4 text-emerald-600" />
            </div>
            <h2 className="text-base font-bold text-gray-900">Buy credits</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg border border-gray-200 hover:bg-gray-50 flex items-center justify-center transition-colors" data-testid="close-purchase-modal">
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        {/* Current Balance pill (soft green) */}
        <div className="px-5 pt-4">
          <div className="flex items-center justify-between bg-emerald-50/60 border border-emerald-100 rounded-xl px-4 py-3" data-testid="modal-balance">
            <div className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-emerald-700" />
              <span className="text-xs text-gray-700 font-semibold">Current balance</span>
              {creditLimit > 0 && (
                <span className="text-[10px] text-gray-500">· line: {creditLimit.toLocaleString()} cr</span>
              )}
            </div>
            <span className={`text-base font-black ${balance < 0 ? 'text-red-600' : 'text-gray-900'}`}>
              {balance.toLocaleString()} <span className="text-xs font-semibold text-gray-500">credits</span>
            </span>
          </div>

          {trigger === 'insufficient' && (
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5" data-testid="insufficient-warning">
              <p className="text-xs text-amber-700 font-medium">You don't have enough credits for this action. Top up to continue.</p>
            </div>
          )}
          {trigger === 'low_balance' && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5" data-testid="low-balance-warning">
              <p className="text-xs text-red-600 font-medium">Your balance is running low. Top up to avoid interruptions.</p>
            </div>
          )}
          {creditLimit > 0 && (
            <p className="text-[10px] text-gray-400 mt-1 px-1">Available right now: {availableCredit.toLocaleString()} cr (incl. credit line)</p>
          )}
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex justify-center py-14">
            <Loader2 className="h-6 w-6 text-emerald-600 animate-spin" />
          </div>
        ) : (
          <div className="px-5 py-4 space-y-4">
            {/* Quick picks */}
            <div>
              <p className="text-[10px] font-bold text-gray-500 tracking-wider mb-2 uppercase">Quick picks</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {packages.map((pkg) => {
                  const credits = (pkg.credits || 0) + (pkg.bonus_credits || 0);
                  const savePct = savePctFor(pkg);
                  const isSelected = pkg.package_id === selectedPkgId;
                  const isPopular = pkg.is_popular;
                  return (
                    <button
                      key={pkg.package_id}
                      onClick={() => handleSelectPkg(pkg.package_id)}
                      className={`relative border rounded-xl p-3 text-center transition-all ${
                        isSelected
                          ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200'
                          : 'border-gray-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/40'
                      }`}
                      data-testid={`modal-pkg-${pkg.package_id}`}
                    >
                      <div className="text-xl font-black text-gray-900 leading-none">{credits.toLocaleString()}</div>
                      <div className="text-[9px] font-semibold text-gray-400 tracking-wider uppercase mt-0.5">credits</div>
                      <div className="text-[11px] text-gray-500 mt-1.5">{pricing.symbol}{pkg.price_usd.toFixed(0)}</div>
                      {(savePct > 0 || isPopular || pkg._tier_label) && (
                        <div className="mt-2">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-bold ${isPopular ? 'bg-emerald-100 text-emerald-700' : 'bg-emerald-50 text-emerald-600 border border-emerald-100'}`}>
                            {pkg._tier_label || (isPopular ? 'Best value' : `Save ${savePct}%`)}
                          </span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              {packages.length === 0 && (
                <div className="text-center py-6 bg-gray-50 rounded-xl text-xs text-gray-400">No packages available yet</div>
              )}
            </div>

            {/* Custom amount */}
            <div>
              <p className="text-[10px] font-bold text-gray-500 tracking-wider mb-2 uppercase">Custom amount</p>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300" />
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={customAmount}
                  onChange={e => handleCustomChange(e.target.value)}
                  placeholder="e.g. 750"
                  className="w-full pl-9 pr-24 py-3 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  data-testid="custom-amount-input"
                />
                {customUsd > 0 && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-md">
                    {totalCredits.toLocaleString()} cr
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-5 py-3 flex items-center justify-between gap-3 rounded-b-2xl">
          <div>
            <div className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">Total</div>
            <div className="text-lg font-black text-gray-900" data-testid="modal-total">{pricing.symbol}{totalUsd.toFixed(2)}</div>
            {pricing.tax_pct > 0 && totalUsd > 0 && (
              <div className="text-[10px] text-gray-400">incl. {pricing.tax_pct}% {pricing.tax_label || 'tax'}</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePay}
              disabled={purchasing || totalUsd < 1}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gray-900 hover:bg-gray-800 text-white text-sm font-bold disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="modal-pay-now"
            >
              {purchasing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-3.5 w-3.5" />}
              Pay now
            </button>
          </div>
        </div>

        <div className="px-5 pb-3 pt-1 flex items-center gap-2">
          <Shield className="h-3 w-3 text-gray-300 shrink-0" />
          <p className="text-[10px] text-gray-400">Secure payment powered by Stripe.</p>
        </div>
      </div>
    </div>
  );
}
